import { useState } from 'react';
import { api, type LinkPreview } from '../../api/client';
import { Icon } from '../../lib/icons';

/** The site's own name, from the address ("news.example.com"), for a preview that doesn't give one. */
function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

/**
 * What a web address in a message shows, under it: the site, the page's title (a link, in a new
 * tab) and description, and its picture. Stitch fetched all of it, picture included: the browser
 * never asks the site for anything. A YouTube video shows its title, channel and picture with a
 * play mark; it opens on YouTube (nothing plays inside Stitch). The sender can take it away.
 */
export function LinkPreviewCard({
  domain,
  conversationId,
  messageId,
  preview,
  canRemove,
  onError
}: {
  domain: string;
  conversationId: string;
  messageId: string;
  preview: LinkPreview;
  /** Their own message: they can take the preview away (for everyone). */
  canRemove: boolean;
  onError: (message: string) => void;
}) {
  const [removing, setRemoving] = useState(false);
  const [gone, setGone] = useState(false);
  if (gone) return null;
  const video = preview.kind === 'youtube';
  const site = preview.siteName || hostOf(preview.url);
  const picture = preview.image ? api.previewImageUrl(domain, conversationId, messageId, preview.image.version) : null;
  const large = Boolean(picture && (video || preview.image?.large));
  const name = preview.title || site;
  const open = { href: preview.url, target: '_blank', rel: 'noopener noreferrer nofollow' } as const;
  return (
    <div className={`msg-preview${large ? ' large' : ''}${video ? ' video' : ''}${picture ? ' with-picture' : ''}`}>
      <div className="msg-preview-text">
        <span className="msg-preview-site">{site}</span>
        {preview.title && (
          <a className="msg-preview-title" {...open}>
            {preview.title}
          </a>
        )}
        {preview.author && <span className="msg-preview-author">{preview.author}</span>}
        {preview.description && <p className="msg-preview-desc">{preview.description}</p>}
      </div>
      {picture && (
        <a className="msg-preview-pic" {...open} aria-label={video ? `Play “${name}” on YouTube` : `Open ${name}`}>
          <img src={picture} alt="" loading="lazy" decoding="async" />
          {video && (
            <span className="msg-preview-play" aria-hidden="true">
              <Icon name="play" size={22} />
            </span>
          )}
        </a>
      )}
      {canRemove && (
        <button
          type="button"
          className="msg-preview-remove"
          aria-label="Remove the preview"
          title="Remove the preview (for everyone)"
          disabled={removing}
          onClick={async () => {
            setRemoving(true);
            try {
              await api.removePreview(domain, conversationId, messageId);
              setGone(true);
            } catch (e) {
              onError(e instanceof Error ? e.message : String(e));
            } finally {
              setRemoving(false);
            }
          }}
        >
          <Icon name="x" size={14} />
        </button>
      )}
    </div>
  );
}
